package com.exura.components.dialogs.imageSelection

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.exura.components.buttons.AppButton
import com.exura.components.buttons.AsyncButton
import com.exura.components.buttons.ButtonGhostVariant
import com.exura.components.icons.ExuraText
import com.exura.components.icons.UntiledIcon
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBodyHeaderTextVariant
import com.exura.components.pageTypography.ExuraSecondaryTextVariant
import com.exura.components.pageTypography.ExuraSectionHeaderTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.components.pageTypography.ListEntryImageLargeVariant
import com.exura.components.pageTypography.ListEntryImageStyle
import com.exura.features.fileStorage.FileStoragePrefixes
import com.exura.features.fileStorage.usecase.ListFilesUseCase
import com.exura.features.fileStorage.usecase.UploadFileUseCase
import com.exura.features.localization.commonLocalization
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toModifier
import dev.gitlive.firebase.storage.File as StorageFile
import kotlinx.browser.document
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.times
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.events.SyntheticChangeEvent
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.File
import org.w3c.files.FilePropertyBag
import org.w3c.files.FileReader
import org.w3c.files.get
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Date

@Composable
fun UserImagesDialogContent(
    initialImageUrl: String? = null,
    onImageSelected: (String?) -> Unit,
    onDismiss: () -> Unit,
) {
    val controller = rememberUserImagesDialogController(initialImageUrl)
    val state = controller.uiState

    LaunchedEffect(Unit) {
        controller.loadImages()
    }

    UserImagesDialogLayout(
        state = state,
        onEvent = controller::handleEvent,
        onImageSelected = onImageSelected,
        onDismiss = onDismiss,
    )
}

@Composable
private fun UserImagesDialogLayout(
    state: UserImagesDialogUiState,
    onEvent: suspend (UserImagesDialogEvent) -> Unit,
    onImageSelected: (String?) -> Unit,
    onDismiss: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(2 * Dimens.dialogContentGap),
    ) {
        state.selectedImageUrl?.let { ImagePreview(it) }
        UserImagesGrid(
            images = state.existingImages,
            isLoadingImages = state.isLoading,
            onImageClicked = { imageUrl ->
                scope.launch { onEvent(UserImagesDialogEvent.SelectImage(imageUrl)) }
            },
        )
        DiskSelectImageButton(
            isFilePending = state.pendingFile != null,
            onFileChanged = { e ->
                val file = e.target.files?.get(0)
                file?.let { f ->
                    scope.launch { onEvent(UserImagesDialogEvent.SelectFile(f)) }
                }
            }
        )
        ConfirmChangesControls(
            isChanged = state.initialImageUrl != state.selectedImageUrl,
            onConfirm = suspend {
                onEvent(UserImagesDialogEvent.ConfirmSelection(onImageSelected, onDismiss))
            },
            onDismiss = onDismiss,
            isUploading = state.isUploading,
        )
    }
}

@Composable
private fun ImagePreview(
    imageSrc: String,
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(Dimens.dialogContentGap),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            commonLocalization.current.crud.preview,
            modifier = ExuraTextStyle.toModifier(ExuraSectionHeaderTextVariant),
        )
        Box(
            modifier = ListEntryImageStyle.toModifier(ListEntryImageLargeVariant),
            contentAlignment = Alignment.Center,
        ) { Image(imageSrc, modifier = Modifier.objectFit(ObjectFit.Contain).fillMaxSize()) }
    }
}

@Composable
private fun UserImagesGrid(
    images: List<String>,
    isLoadingImages: Boolean,
    onImageClicked: (String) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .minWidth(Dimens.spacingNone)
            .overflow(Overflow.Hidden),
        verticalArrangement = Arrangement.spacedBy(Dimens.dialogContentGap),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SpanText(
            "${commonLocalization.current.crud.available}:",
            modifier = ExuraTextStyle.toModifier(ExuraBodyHeaderTextVariant),
        )

        when {
            isLoadingImages -> Loader()
            images.isNotEmpty() -> Row(
                modifier = ImageSelectionRibbonStyle.toModifier(),
                horizontalArrangement = Arrangement.spacedBy(Dimens.imageSelectionDialogGap),
            ) {
                images.forEach { imageSrc ->
                    Box(
                        modifier = ListEntryImageStyle
                            .toModifier()
                            .cursor(Cursor.Pointer)
                            .onClick { onImageClicked(imageSrc) },
                        contentAlignment = Alignment.Center,
                    ) {
                        Image(imageSrc, modifier = Modifier.objectFit(ObjectFit.Contain).fillMaxSize())
                    }
                }
            }
            else ->  SpanText(
                commonLocalization.current.crud.notFound,
                modifier = ExuraTextStyle.toModifier(ExuraSecondaryTextVariant),
            )
        }
//        if (images.isNotEmpty()) {
//            Row(
//                modifier = ImageSelectionRibbonStyle.toModifier(),
//                horizontalArrangement = Arrangement.spacedBy(Dimens.imageSelectionDialogGap),
//            ) {
//                images.forEach { imageSrc ->
//                    Box(
//                        modifier = ListEntryImageStyle
//                            .toModifier()
//                            .cursor(Cursor.Pointer)
//                            .onClick { onImageClicked(imageSrc) },
//                        contentAlignment = Alignment.Center,
//                    ) {
//                        Image(imageSrc, modifier = Modifier.objectFit(ObjectFit.Contain).fillMaxSize())
//                    }
//                }
//            }
//        } else {
//            SpanText(
//                commonLocalization.current.crud.notFound,
//                modifier = ExuraTextStyle.toModifier(ExuraSecondaryTextVariant),
//            )
//        }
    }
}

@Composable
private fun DiskSelectImageButton(
    isFilePending: Boolean,
    onFileChanged: (SyntheticChangeEvent<String, HTMLInputElement>) -> Unit,
) {
    Input(
        type = InputType.File,
        attrs = Modifier
            .display(DisplayStyle.None)
            .id("hidden-file-input")
            .toAttrs {
                accept("image/png, image/jpeg, image/svg+xml")
                onChange { onFileChanged(it) }
            }
    )
    AppButton(
        text = if (!isFilePending) commonLocalization.current.controls.uploadFromComputer
        else commonLocalization.current.controls.changeFile,
        onClick = {
            document
                .getElementById("hidden-file-input")
                ?.unsafeCast<HTMLInputElement>()?.click()
        }
    )
}

@Composable
private fun ConfirmChangesControls(
    isChanged: Boolean,
    isUploading : Boolean,
    onConfirm: suspend () -> Unit,
    onDismiss: () -> Unit,
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(Dimens.dialogControlsGap),
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
            .justifyContent(JustifyContent.Center)
    ) {
        AsyncButton(
            text = commonLocalization.current.controls.confirmChanges,
            onClick = onConfirm,
            icon = { UntiledIcon(Assets.IconsUntiled.CHECK_MARK) },
            isDisabled = !isChanged,
        )
        AppButton(
            text = commonLocalization.current.controls.reject,
            onClick = onDismiss,
            variant = ButtonGhostVariant,
            icon = { UntiledIcon(Assets.IconsUntiled.CROSS_MARK) },
            isDisabled = isUploading,
        )
    }
}


val ImageSelectionRibbonStyle = CssStyle {
    base {
        Modifier
            .minWidth(Dimens.spacingNone)
            .fillMaxWidth()
            .overflow(Overflow.Auto)
            .padding(
                leftRight = Dimens.imageSelectionDialogIndent,
                topBottom = Dimens.imageSelectionDialogWrapping,
            )
    }
}
